import React, { useCallback, useMemo, useRef } from 'react';
import { AbstractHoverZoneGroupedProps, DragData, ElementOffset } from './types';
import { throttle } from 'lodash';
import { AbstractHoverZoneBase } from './AbstractHoverZoneBase';

type Group<D> = {
  xTick: number;
  points: D[];
};

export const AbstractHoverZoneGrouped = <D,>({
  children,
  dataPoints,
  xScale,
  dataToX,
  yScale,
  dataToY,
  tooltipCoordinateData,
  setTooltipCoordinateData,
  setDragData,
  ...rest
}: AbstractHoverZoneGroupedProps<D>) => {
  const groups: Group<D>[] = useMemo(() => {
    const map = dataPoints.reduce<Map<number, D[]>>((acc, point) => {
      const x = xScale(dataToX(point));
      const groupData = acc.get(x) ?? [];
      acc.set(x, [...groupData, point]);
      return acc;
    }, new Map<number, D[]>());

    return Array.from(map).map(([x, points]) => ({
      xTick: x,
      points,
    }));
  }, [dataPoints, dataToX, xScale]);

  const dragData = useRef<DragData<D>>({
    isDragging: false,
    startX: null,
    endX: null,
  });

  const throttleMouseMove = useMemo(
    () =>
      throttle((e, setTooltipCoordinateData, setDragData, elementOffset) => {
        if (!elementOffset) return;

        const { top } = elementOffset;
        const y = e.pageY - top;

        const closestGroup = findClosestGroup(e, elementOffset, groups);
        if (!closestGroup) {
          return;
        }

        // closest point comes from the points within the group
        let closestPoint = closestGroup.points[0]!;
        let closestPointY = yScale(dataToY(closestGroup.points[0]!));
        let closestPointDistance = Math.abs(closestPointY - y);
        for (let i = 1; i < closestGroup.points.length; i++) {
          const point = closestGroup.points[i]!;
          const pointY = yScale(dataToY(point));
          const distance = Math.abs(pointY - y);
          if (distance < closestPointDistance) {
            closestPoint = point;
            closestPointY = pointY;
            closestPointDistance = distance;
          }
        }

        setTooltipCoordinateData({
          x: closestGroup.xTick,
          y: closestPointY,
          closestPoint,
          groupedPoints: closestGroup.points,
        });

        if (dragData.current.isDragging) {
          dragData.current = {
            isDragging: true,
            startX: dragData.current.startX,
            endX: closestGroup.xTick,
          };
          setDragData?.(dragData.current);
        }
      }, 16),
    [dataToY, groups, yScale],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent, elementOffset: ElementOffset) => {
      const closestGroup = findClosestGroup(e, elementOffset, groups);
      if (!closestGroup) {
        return;
      }

      // if we're already dragging but lost focus, update the end but keep the start
      if (dragData.current.isDragging) {
        dragData.current = {
          isDragging: false,
          startX: dragData.current.startX,
          endX: closestGroup.xTick,
        };
      } else {
        dragData.current = {
          isDragging: true,
          startX: closestGroup.xTick,
          endX: null,
        };
      }

      setDragData?.(dragData.current);
    },
    [groups, setDragData],
  );

  const onMouseUp = useCallback(
    (e: React.MouseEvent, elementOffset: ElementOffset) => {
      const closestGroup = findClosestGroup(e, elementOffset, groups);

      /**
       *  if the user is just single clicking, they're likely trying to de-select the hover zone,
       *  so in these cases we will completely reset the dragData to remove the old selection
       */
      const newEndX = closestGroup?.xTick ?? null;
      if (newEndX !== dragData.current.startX) {
        dragData.current = {
          isDragging: false,
          startX: dragData.current.startX,
          endX: newEndX,
        };
      } else {
        dragData.current = {
          isDragging: false,
          startX: null,
          endX: null,
        };
      }
      setDragData?.(dragData.current);
    },
    [groups, setDragData],
  );

  return (
    <AbstractHoverZoneBase
      data-component="AbstractHoverZoneGrouped"
      {...rest}
      positionKey={`${tooltipCoordinateData?.x}${tooltipCoordinateData?.y}`}
      onMouseLeave={() => {
        throttleMouseMove.cancel();
        setTooltipCoordinateData(null);
      }}
      onMouseMove={(e, elementOffset) =>
        throttleMouseMove(e, setTooltipCoordinateData, setDragData, elementOffset)
      }
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      tooltipX={tooltipCoordinateData?.x}
      tooltipY={tooltipCoordinateData?.y}
      tooltipData={tooltipCoordinateData?.groupedPoints ?? null}
    >
      {children}
    </AbstractHoverZoneBase>
  );
};

function findClosestGroup<D>(
  e: React.MouseEvent,
  elementOffset: ElementOffset,
  groups: Group<D>[],
): Group<D> | undefined {
  let closestGroup = groups[0];
  if (!closestGroup || !elementOffset) {
    return;
  }

  const { left } = elementOffset;
  const x = e.pageX - left;
  let closestDistance = Math.abs(closestGroup.xTick - x);
  for (let i = 1; i < groups.length; i++) {
    const group = groups[i]!;
    const distance = Math.abs(group.xTick - x);
    if (distance < closestDistance) {
      closestGroup = group;
      closestDistance = distance;
    }
  }

  return closestGroup;
}
